/*
 *   Copyright (c) 2020 BuserNet Consulting LLC
 *   All rights reserved.
 */
import _forEach from 'lodash/forEach';
import { lineString as turfLineString } from '@turf/turf';
import d3 from '../../globalexports/d3.export';
import { configureCanvas, getHiDPICanvasConfig, clearCanvas } from '../../utils/canvas';
import bnd3Service from '../../services/bnd3.service';
import EventConst from '../../constants/event.constant';
import GlobeConst from '../../constants/globe.constant';
import AppConst from '../../constants/app.constant';
import ChartConst from '../../constants/chart.constant';
import utilityService from '../../services/utility.service';
import { is_iphone } from '../../services/utils/is_iphone';
import { is_ipad } from '../../services/utils/is_ipad';
import metarDataService from '../../services/metarData.service';
import mosDataService from '../../services/mosData.service';
import stormTrackDataService from './stormTracks/stormTrackDataService';
import routeProfileService from '../Routeprofile/Routeprofile.service';
import globeviewUtilityService from './MapviewUtility.service';
import projectionTester from './MapviewTester.service';
import Emitter from '../../bus';
import ConfigDeviceSettings from '../../config/device.config';
import { drawMainLandBorder } from './renderer/renderGlobeBase/drawMainLandBorder';
import { setLineDash } from './renderer/utils/setLineDash';
import { drawPlaneForTravelRoute } from './renderer/renderTravelRoute/drawPlaneForTravelRoute';
import { drawTravelRouteDots } from './renderer/renderTravelRoute/drawTravelRouteDots';
import { getInBounds } from './renderer/utils/getInBounds';
import { drawRadarImgeOnCanvas } from './renderer/renderRadar/drawRadarImgeOnCanvas';
import { drawMetarDot } from './renderer/renderMetarMos/drawMetarDot';
import { drawUSAStates } from './renderer/renderGlobeBase/drawUSAStates';
import { setPointRadius } from './renderer/utils/setPointRadius';
import { drawAviationMetPolygon } from './renderer/renderMets/drawAviationMetPolygon';
import { drawGeoPathForRoute } from './renderer/renderTravelRoute/drawGeoPathForRoute';
import { drawStormTiming } from './renderer/renderStormTracks/drawStormTiming';
import { drawMosDot } from './renderer/renderMetarMos/drawMosDot';
import { drawUserLocation } from './renderer/renderUser/drawUserLocation';
import { getUserLocationFromStore } from '../../services/capacitor/getUserLocationFromStore';
import { drawCenterOfViewPort } from './renderer/renderCommon/drawCenterOfViewPort';
import { getStormsToRender } from './stormTracks/utils/getStormsToRender';
import { drawStorm } from './renderer/renderStormTracks/drawStorm';
import { getMrmsImages } from '../../services/mrms/getMrmsImages';
import { getMapMosCurrentSlice } from '../../services/mos/getMapMosCurrentSlice';
import * as LayerUpdateTimeService from '../../services/layerUpdateTime/layerUpdateTime.service';

/**
 * @typedef RenderingOptions
 * @type {Object}
 * @property {Boolean} shouldDrawGlobe
 * @property {Boolean} shouldDrawRadar
 * @property {Boolean} shouldDrawFeatures
 * @property {Boolean} shouldFetchFreshRecords
 * @property {Boolean} shouldCleanRadar
 * @property {Boolean} shouldCleanAllRadar
 * @property {Boolean} shouldDrawAllRadar
 * @property {Boolean} isZoomEnd
 */

/**
 * Root service function
 */
function globeviewService() {
  const content = {};
  content.settings = {};
  content.sourceData = null;
  content.flightCoverageDetails = null;
  content.radarSliderDetails = null;
  content.etdSliderDetails = null;
  let zooming = false;
  const MARGIN = 20;
  let width;
  let height;
  let margin;
  let sliderHeight = 0;
  const globeCanvasConfig = {
    canvas: null,
    ctx: null,
    path: null,
  };
  const radarCanvasCollection = [];
  const featuresCanvasConfig = {
    canvas: null,
    ctx: null,
    path: null,
  };
  const stormTrackCanvasConfig = {
    canvas: null,
    ctx: null,
    path: null,
  };
  const allRadarSiteCanvasConfig = {
    canvas: null,
    ctx: null,
    path: null,
  };
  let scale;
  let scaleThreshold;
  let scaleThresholdForMetar;
  let scaleThresholdForRouteIcaoID;
  let projection;
  // let scaleExtent;
  // let land;
  let usaStates;
  let lineStringPointsGeoJson;
  const lastDraggedPoint = 0;
  let mouseClickPoints;
  let highestExpiry;
  let metsToBeDrawn;
  let radarSitesToBeDrawn;
  let lastRadarSiteDrawn = null;
  let dateFormat;
  let clickLngLatPoint;
  let selectedTooltip = {};
  let refAirportTooltipPoint;
  let currentActiveSlider = null;
  function loadRadarCanvasCollection() {
    let i = 0;
    for (i = 0; i < GlobeConst.radarSlidesCount; i += 1) {
      radarCanvasCollection.push({
        canvas: null,
        ctx: null,
        path: null,
        isClear: true,
      });
    }
  }
  loadRadarCanvasCollection();
  const resetTooltip = () => {
    selectedTooltip = {};
  };
  content.resetRadarCanvasCollection = () => {
    resetTooltip();
    radarCanvasCollection.forEach((item) => {
      item.isClear = true;
    });
  };
  function setDateFormat() {
    if (content.settings.timeZone === AppConst.timeZones.local.key) {
      dateFormat = d3.timeFormat(
        ChartConst.xAxis.formatLocal + ChartConst.GraphConfig.xAxis.timePostFix.local,
      );
    } else {
      dateFormat = d3.utcFormat(
        ChartConst.xAxis.format + ChartConst.GraphConfig.xAxis.timePostFix.zulu,
      );
    }
  }
  content.etdSliderChanging = () => {
    /**
     * If not showing latest ETD then hide radar canvas
     */
    if (content.etdSliderDetails.idx > 0) {
      d3.select('#map-canvas-radar').style('display', 'none');
    } else {
      d3.select('#map-canvas-radar').style('display', 'block');
    }
  };
  content.flightSliderChanging = () => {
    if (content.flightCoverageDetails.idx > 0 || content.etdSliderDetails.idx > 0) {
      d3.select('#map-canvas-radar').style('display', 'none');
      d3.select('#map-canvas-stormtracks').style('display', 'none');
    } else {
      d3.select('#map-canvas-radar').style('display', 'block');
      d3.select('#map-canvas-stormtracks').style('display', 'block');
    }
  };
  content.showHideRadarForSlider = () => {
    radarCanvasCollection.forEach((item, index) => {
      d3.select(`#map-canvas-radar #canvas-${index}`).style('display', 'none');
    });
    // If radar slider not showing latest then hide stormtrack
    if (content.radarSliderDetails.idx < GlobeConst.radarSlidesCount - 1) {
      //   d3.select('#map-canvas-features').style('display', 'none');
      d3.select('#map-canvas-stormtracks').style('display', 'none');
    } else {
      //   d3.select('#map-canvas-features').style('display', 'block');
      d3.select('#map-canvas-stormtracks').style('display', 'block');
    }
    d3.select(`#map-canvas-radar #canvas-${content.radarSliderDetails.idx}`).style(
      'display',
      'block',
    );
  };
  content.drawUserLocation = () => {
    const userLocation = getUserLocationFromStore();
    if (userLocation) {
      const { coords } = userLocation;
      drawUserLocation(projection, featuresCanvasConfig.ctx, [coords.longitude, coords.latitude]);
    }
  };
  content.drawBasicGlobe = () => {
    // Drawing main globe sphere
    // drawMainGlobeSphere(globeCanvasConfig.ctx, globeCanvasConfig.path);

    // Drawing main land
    // drawMainLand(globeCanvasConfig.ctx, globeCanvasConfig.path, land);

    // setLineDash(globeCanvasConfig.ctx, [5, 5]);

    // // Drawing main land borders
    drawMainLandBorder(globeCanvasConfig.ctx, globeCanvasConfig.path, usaStates);

    // // Drawing US states
    drawUSAStates(
      globeCanvasConfig.ctx,
      globeCanvasConfig.path,
      projection,
      scaleThreshold,
      usaStates,
    );
    // setLineDash(globeCanvasConfig.ctx, []);
  };
  content.populateLineString = () => {
    lineStringPointsGeoJson = turfLineString(
      globeviewUtilityService.getLineString(content.settings),
    );
  };
  content.drawTravelRouteDots = () => {
    const metarDataModel = metarDataService.getMetarDataModel();
    const isFirstSlide = content.isFirstEtdSlide();
    if (content.sourceData && content.sourceData.travelRoute) {
      setPointRadius(
        featuresCanvasConfig.path,
        globeviewUtilityService.getFlightDotsSizes().flightDotRadius,
      );
      content.sourceData.travelRoute.forEach((airport) => {
        const airportData = {
          ...airport,
        };
        if (isFirstSlide && content.flightCoverageDetails?.idx === 0) {
          airportData.frl = metarDataModel?.allMetarMap[airport.icao_id] || airport.frl;
        }
        drawTravelRouteDots(
          featuresCanvasConfig.ctx,
          featuresCanvasConfig.path,
          projection,
          airportData,
          scaleThresholdForRouteIcaoID,
          content.settings,
        );
      });
    }
    content.drawUserLocation();
  };

  /**
   * This method returns object with keys only of those
   * airport which are visible in bounding box
   * @param [] allPoints [lat, lng, icao_id]
   * @returns {icaoId: 1} like: {KBTP: 1}
   */
  const getIcaoMapToBeDrawn = (allPoints) => {
    /**
     * Negative values preent cliping of polygons at bounding box
     */
    projection.clipExtent([
      [-10, -10],
      [width + 10, height + 10],
    ]);
    const pointsThatWillBeShown = allPoints.filter(projectionTester(projection));
    const icaoMap = pointsThatWillBeShown.reduce((acc, item) => {
      acc[item[2]] = 1;
      return acc;
    }, {});
    return icaoMap;
  };

  const getTravelRouteAirportMap = () => content.sourceData?.airportSequence?.reduce((
    acc,
    item,
  ) => {
    acc[item] = 1;
    return acc;
  }, {}) || {};

  content.drawMosDots = (data) => {
    setPointRadius(
      featuresCanvasConfig.path,
      globeviewUtilityService.getFlightDotsSizes().flightDotMetar,
    );
    if (
      data.mapMosDataObj
      && content.sourceData
      && content.sourceData.travelRoute
      && content.sourceData.travelRoute[0]
    ) {
      const mapMosRecords = getMapMosCurrentSlice(
        content.sourceData.travelRoute[0],
        data.mapMosDataObj,
        content.sourceData,
        content.settings,
        lastDraggedPoint,
      );
      const icaoMap = getIcaoMapToBeDrawn(data.allPoints);
      const travelRouteMap = getTravelRouteAirportMap();
      // projection.clipExtent([[0, 0], [width, height]]);
      mapMosRecords.forEach((airport) => {
        // const point = [
        //   [airport.lng, airport.lat],
        // ];
        // if (point.filter(globeviewTesterService.tester(projection)).length === 1) {
        if (icaoMap[airport.icao_id] === 1 && !travelRouteMap[airport.icao_id]) {
          drawMosDot(
            featuresCanvasConfig.ctx,
            featuresCanvasConfig.path,
            airport,
            projection,
            scaleThresholdForRouteIcaoID,
          );
        }
        // }
      });
    }
  };
  content.isZoomedEnoughToDrawMetar = () => projection.scale() > scaleThresholdForMetar;

  content.needToDrawMetarDot = () => {
    if (content.settings.departureTime - content.settings.firstDateObj < 10) {
      return true;
    }
    return false;
  };
  content.drawMetarDots = (data) => {
    const isFirstSlide = content.isFirstEtdSlide();
    // Uncomment below if want to show METAR if zoomed enough
    // const isZoomedEnoughToDrawMetar = content.isZoomedEnoughToDrawMetar();
    // const canDrawMetar = isFirstSlide && isZoomedEnoughToDrawMetar;
    const canDrawMetar = isFirstSlide;
    if (canDrawMetar && globeviewUtilityService.shouldShowMetarAndMos()) {
      setPointRadius(
        featuresCanvasConfig.path,
        globeviewUtilityService.getFlightDotsSizes().flightDotMetar,
      );
      /**
       * Negative values preent cliping of polygons at bounding box
       */
      const icaoMap = getIcaoMapToBeDrawn(data.allPoints);
      const travelRouteMap = getTravelRouteAirportMap();
      if (data.metarDataObj) {
        data.metarDataObj?.forEach((airport) => {
          if (icaoMap[airport.icao_id] === 1 && !travelRouteMap[airport.icao_id]) {
            drawMetarDot(
              featuresCanvasConfig.ctx,
              featuresCanvasConfig.path,
              airport,
              projection,
              scaleThresholdForRouteIcaoID,
            );
          }
        });
      }
    }
  };

  // This method is needed to makesure if data is available then
  // No async request is needed, as it will cause flicker
  content.startStormTrackRendering = () => {
    stormTrackDataService.getData().then((data) => {
      if (content.isFirstEtdSlide()) {
        content.drawStormTracks(data);
      }
    });
  };

  // This method is needed to makesure if data is available then
  // No async request is needed, as it will cause flicker
  content.startMetarDotsRendering = () => {
    const metarDataModel = metarDataService.getMetarDataModel();
    if (metarDataModel.metarDataObj) {
      content.drawMetarDots(metarDataModel);
    } else {
      metarDataService.getData().then((data) => {
        content.drawMetarDots(data);
      });
    }
  };

  content.drawPlaneForTravelRoute = () => {
    drawPlaneForTravelRoute(
      featuresCanvasConfig.ctx,
      projection,
      lineStringPointsGeoJson,
      content.flightCoverageDetails,
    );
  };
  content.drawGeoPathForRoute = () => {
    if (content.settings) {
      content.populateLineString();
      drawGeoPathForRoute(
        featuresCanvasConfig.ctx,
        featuresCanvasConfig.path,
        lineStringPointsGeoJson,
      );
    }
  };
  // const drawRadarSiteUsed = (radarSiteToUse) => {
  //   setPointRadius(allRadarSiteCanvasConfig.path, 10);
  //   drawStormDot(
  //     allRadarSiteCanvasConfig.ctx,
  //     allRadarSiteCanvasConfig.path,
  //     turfPoint([radarSiteToUse.lat, radarSiteToUse.lng]),
  //     '#39ff14',
  //   );
  // };

  content.drawStormTracks = (stormTrackData) => {
    drawCenterOfViewPort(stormTrackCanvasConfig, projection, width, height);
    if (stormTrackData && stormTrackData.allStorms) {
      radarSitesToBeDrawn = getStormsToRender(stormTrackData, projection, width, height, scale);
      if (!radarSitesToBeDrawn) {
        return;
      }
      // drawRadarSiteUsed(radarSitesToBeDrawn);
      if (
        lastRadarSiteDrawn
        && radarSitesToBeDrawn
        && radarSitesToBeDrawn.id !== lastRadarSiteDrawn
      ) {
        content.clearTooltip();
        resetTooltip();
      }
      lastRadarSiteDrawn = radarSitesToBeDrawn.id;
      setDateFormat();
      drawStorm(
        radarSitesToBeDrawn,
        stormTrackCanvasConfig,
        selectedTooltip,
        projection,
        dateFormat,
      );
    }
  };
  content.drawAviationMet = () => {
    metsToBeDrawn = [];
    if (content.sourceData.aviationMet) {
      _forEach(content.sourceData.aviationMet, (airmet) => {
        if (!highestExpiry) {
          highestExpiry = airmet.expireOnLocal;
        } else if (airmet.expireOnLocal > highestExpiry) {
          highestExpiry = airmet.expireOnLocal;
        }
        if (globeviewUtilityService.shouldShowAirmet(airmet)) {
          if (
            globeviewUtilityService.checkIfItIsValidAviationMet(
              airmet,
              content.settings.departureTime,
            )
          ) {
            metsToBeDrawn.push(airmet);
            drawAviationMetPolygon(
              featuresCanvasConfig.ctx,
              featuresCanvasConfig.path,
              airmet,
              projection,
            );
          }
        }
      });
    }
    setLineDash(featuresCanvasConfig.ctx, []);
    featuresCanvasConfig.ctx.globalAlpha = 1;
  };

  function drawRadarImageOnCanvas(slide, drawing) {
    /**
     * This will make sure that only those canvas which
     * are not already drawn are rendered
     */
    if (!drawing || !projection) {
      return;
    }
    if (radarCanvasCollection[slide] && radarCanvasCollection[slide].isClear) {
      radarCanvasCollection[slide].isClear = false;
      const bb = {
        north: 55.0,
        east: -60.000002,
        south: 20.000001,
        west: -130.0,
      };
      const pUl = projection([bb.west, bb.north]).map((x) => x);
      const pLr = projection([bb.east, bb.south]).map((x) => x);
      drawRadarImgeOnCanvas(
        radarCanvasCollection[slide].ctx,
        pLr[0] - pUl[0],
        pLr[1] - pUl[1],
        pUl[0],
        pUl[1],
        drawing,
      );
    }
  }

  function drawAsyncRasterImage(i, image) {
    window.requestAnimationFrame(() => {
      if (!zooming) {
        drawRadarImageOnCanvas(i, image);
        LayerUpdateTimeService.setCurrentScreenRenderTime();
      }
    });
  }
  /**
   *
   * @param {Object} radarItem
   */
  function getBestImageObjectLoaded(radarItem) {
    return radarItem ? radarItem.radarDrawingLow : null;
  }

  /**
   * This function just draw radar of current active slider index
   * @param {Array} radarImageCollection Collection of radar images
   */
  function drawCurrentSlideRadar(radarImageCollection) {
    drawRadarImageOnCanvas(
      content.radarSliderDetails.idx,
      getBestImageObjectLoaded(radarImageCollection[content.radarSliderDetails.idx]),
    );
  }
  /**
   * This function  draw radar of all non vsisible slides
   * @param {Array} radarImageCollection Collection of radar images
   */
  const drawAllRadarNonVisibleSlides = () => {
    const radarImageCollection = getMrmsImages();
    for (let i = 0; i < GlobeConst.radarSlidesCount; i += 1) {
      if (content.radarSliderDetails && i !== content.radarSliderDetails.idx) {
        drawAsyncRasterImage(i, getBestImageObjectLoaded(radarImageCollection[i]));
      }
    }
  };

  /**
   * @function
   * @param {RenderingOptions} options
   */
  function renderRadar(options) {
    const radarImageCollection = getMrmsImages();
    /**
     * Draw all radar slides only when current active slider is radar slider
     */
    if (options.isZoomEnd && currentActiveSlider === 'radar') {
      if (options.shouldDrawAllRadar) {
        drawCurrentSlideRadar(radarImageCollection);
      }
      drawAllRadarNonVisibleSlides();
    } else if (content.radarSliderDetails) {
      drawCurrentSlideRadar(radarImageCollection);
    }
  }

  content.sliderTypeChanged = (slider) => {
    currentActiveSlider = slider;
    switch (slider) {
      case 'etd':
        d3.select('#map-canvas-features').style('display', 'block');
        break;
      case 'radar':
        drawAllRadarNonVisibleSlides();
        break;
      case 'flight':
        break;
      default:
    }
  };
  Emitter.on(EventConst.radarImageLoaded, () => {
    if (ConfigDeviceSettings.settings.enableRadarFeature) {
      if (globeviewUtilityService.shouldShowRadar()) {
        const radarImageCollection = getMrmsImages();
        radarImageCollection.forEach((item, i) => {
          if (item.radarDrawingLow.isRadarImageLoadSuccess) {
            drawAsyncRasterImage(i, item.radarDrawingLow);
          }
        });
      }
    }
  });

  content.isFirstEtdSlide = () => content.etdSliderDetails && content.etdSliderDetails.idx === 0;

  /**
   * @function
   * @param {RenderingOptions} options
   */
  function renderFeatures(options) {
    if (content.sourceData) {
      clearCanvas(featuresCanvasConfig.ctx, featuresCanvasConfig.canvas);
      clearCanvas(stormTrackCanvasConfig.ctx, stormTrackCanvasConfig.canvas);
      clearCanvas(allRadarSiteCanvasConfig.ctx, allRadarSiteCanvasConfig.canvas);
      radarSitesToBeDrawn = null;
      content.drawAviationMet();
      setLineDash(featuresCanvasConfig.ctx, []);
      if (globeviewUtilityService.shouldShowMetarAndMos()) {
        content.startMosDotsRendering(options);
      } else {
        // projection.clipExtent(null);
      }
      content.startMetarDotsRendering();
      if (
        ConfigDeviceSettings.settings.enableRadarFeature
        && globeviewUtilityService.shouldShowStormtrack()
      ) {
        content.startStormTrackRendering();
      }
      content.drawGeoPathForRoute();
      content.drawTravelRouteDots();
      content.drawPlaneForTravelRoute();
    }
  }
  /**
   * This function helps to center current location
   *
   */
  content.getCurrentLocationParticle = () => {
    const userLocation = getUserLocationFromStore();
    if (userLocation) {
      const { coords } = userLocation;
      const offset = 0.8;
      return turfLineString([
        [coords.longitude - offset, coords.latitude - offset],
        [coords.longitude, coords.latitude],
        [coords.longitude + offset, coords.latitude + offset],
      ]);
    }
    return null;
  };
  /**
   * @function
   * @param {RenderingOptions} options
   */
  function renderGlobe(options) {
    clearCanvas(globeCanvasConfig.ctx, globeCanvasConfig.canvas);
    if (options.shouldGetInBoundToRoute === true) {
      content.populateLineString();
      getInBounds(globeCanvasConfig, width, height, projection, lineStringPointsGeoJson);
    } else if (options.shouldGetInBoundToCurrentPosition) {
      const data = content.getCurrentLocationParticle();
      if (data) {
        getInBounds(globeCanvasConfig, width, height, projection, data);
      }
    }
    content.drawBasicGlobe();
  }
  /**
   * @function
   * @param {RenderingOptions} options
   */
  function clearAllRadarCanvas(options) {
    for (let i = 0; i < GlobeConst.radarSlidesCount; i += 1) {
      if (options.isZoomEnd && !options.shouldCleanAllRadar) {
        if (content.radarSliderDetails && i !== content.radarSliderDetails.idx) {
          radarCanvasCollection[i].isClear = true;
          clearCanvas(radarCanvasCollection[i].ctx, radarCanvasCollection[i].canvas);
        }
      } else {
        radarCanvasCollection[i].isClear = true;
        clearCanvas(radarCanvasCollection[i].ctx, radarCanvasCollection[i].canvas);
      }
    }
  }
  content.clearAllRadarCanvasHandle = () => {
    for (let i = 0; i < GlobeConst.radarSlidesCount; i += 1) {
      if (radarCanvasCollection && radarCanvasCollection[i] && radarCanvasCollection[i].ctx) {
        radarCanvasCollection[i].isClear = true;
        clearCanvas(radarCanvasCollection[i].ctx, radarCanvasCollection[i].canvas);
      }
    }
  };
  /**
   * @function
   * @param {RenderingOptions} options
   */
  function renderInterface(options) {
    if (!options) {
      options = {};
    }
    if (options.shouldDrawGlobe) {
      renderGlobe(options);
    }
    if (options.shouldCleanRadar) {
      clearAllRadarCanvas(options);
    }
    if (ConfigDeviceSettings.settings.enableRadarFeature && options.shouldDrawRadar) {
      if (globeviewUtilityService.shouldShowRadar()) {
        renderRadar(options);
      }
    }
    if (options.shouldDrawFeatures) {
      renderFeatures(options);
    }
  }

  // function printCurrentScalePercent() {
  //   const zoomPercent = globeviewUtilityService.getZoomPercent(scaleExtent, projection.scale());
  //   d3.select('.scalepercent-container').html(zoomPercent.toFixed(2));
  // }
  /**
   * @function
   * @param {RenderingOptions} options
   */
  function requestDrawing(options) {
    window.requestAnimationFrame(() => {
      // printCurrentScalePercent();
      if (globeCanvasConfig && globeCanvasConfig.ctx && featuresCanvasConfig.ctx) {
        renderInterface(options);
        LayerUpdateTimeService.setCurrentScreenRenderTime();
      }
    });
  }

  // This method is needed to makesure if data is available then
  // No async request is needed, as it will cause flicker
  /**
   * @function startMosDotsRendering
   * @param {Object} options
   * @param {boolean} options.shouldFetchFreshRecords
   */
  content.startMosDotsRendering = (options) => {
    if (options.shouldFetchFreshRecords) {
      mosDataService
        .getData(
          globeviewUtilityService.calculateBoundingBox(projection, width, height),
          projection.scale(),
        )
        .then(() => {
          if (!zooming) {
            requestDrawing({
              shouldDrawGlobe: false,
              shouldDrawRadar: false,
              shouldDrawFeatures: true,
            });
          }
        });
    }
    const mosDataModel = mosDataService.getMosDataModel();
    if (mosDataModel.mapMosDataObj) {
      content.drawMosDots(mosDataModel);
    }
  };

  function setDimensions() {
    const hideBottomAxis = utilityService.hideBottomAxis();
    margin = is_iphone() ? ChartConst.GraphConfig.marginMobile : ChartConst.GraphConfig.margin;
    margin = is_ipad() ? ChartConst.GraphConfig.marginIPad : margin;
    margin.left = utilityService.getSafeAreaPadding().left;
    margin.right = utilityService.getSafeAreaPadding().right;
    if (hideBottomAxis) {
      margin.right += utilityService.getSliderHeight();
    }
    if (hideBottomAxis) {
      sliderHeight = 0;
    } else {
      sliderHeight = utilityService.getSliderHeight();
    }
    width = window.innerWidth
      - (utilityService.is_small_device() ? 0 : parseFloat(AppConst.sideMenuWidth))
      - margin.left
      - margin.right;
    if (utilityService.hideBottomAxis()) {
      width -= utilityService.getHeaderHeight();
    }
    height = window.innerHeight
      - utilityService.getHeaderHeight()
      - utilityService.getFooterHeight()
      - utilityService.iPhoneXFooterExtraOffset()
      - sliderHeight;
  }

  function setScales() {
    scale = Math.min(width, height) / 2 - MARGIN;
    scaleThreshold = scale * GlobeConst.scaleMultiplierForBorders;
    scaleThresholdForMetar = scale * GlobeConst.scaleMultiplierForMetar;
    scaleThresholdForRouteIcaoID = scale * GlobeConst.scaleMultiplierForRouteIcaoID;
    projection = d3
      .geoEquirectangular()
      .precision(0.1)
      .translate([width / 2, height / 2]);
  }
  content.findMatchedTravelRouteAirportAtClickPoint = (lngLat) => {
    if (content.sourceData) {
      return globeviewUtilityService.findMatchedAirportFromListAtGivenLatLng(
        lngLat,
        content.sourceData.travelRoute,
        globeviewUtilityService.getFlightDotsSizes().flightDotRadius,
        projection,
      );
    }
  };
  content.findMatchedMosAirportAtClickPoint = (lngLat) => {
    const mosDataModel = mosDataService.getMosDataModel();
    if (mosDataModel.mapMosDataObj) {
      const mapMosRecords = getMapMosCurrentSlice(
        content.sourceData.travelRoute[0],
        mosDataModel.mapMosDataObj,
        content.sourceData,
        content.settings,
        lastDraggedPoint,
      );
      return globeviewUtilityService.findMatchedAirportFromListAtGivenLatLng(
        lngLat,
        mapMosRecords,
        globeviewUtilityService.getFlightDotsSizes().flightDotRadius,
        projection,
      );
    }
    return null;
  };
  content.findMatchedMetarAirportAtClickPoint = (lngLat) => {
    if (content.needToDrawMetarDot()) {
      const metarDataModel = metarDataService.getMetarDataModel();
      if (metarDataModel.metarDataObj) {
        return globeviewUtilityService.findMatchedAirportFromListAtGivenLatLng(
          lngLat,
          metarDataModel.metarDataObj,
          globeviewUtilityService.getFlightDotsSizes().flightDotRadius,
          projection,
        );
      }
    }
    return null;
  };
  content.findMatchedMetsAtClickPoint = (lngLatPoint) => {
    const metsForTooltips = [];
    if (metsToBeDrawn) {
      metsToBeDrawn.forEach((item) => {
        const contains = d3.geoContains(item, [lngLatPoint.lng, lngLatPoint.lat]);
        if (contains) {
          metsForTooltips.push(item);
        }
      });
    }
    return metsForTooltips;
  };
  content.findMatchedStormAtClickPoint = (lngLatPoint) => {
    if (radarSitesToBeDrawn) {
      return globeviewUtilityService.findMatchedStormAtGivenLatLng(
        lngLatPoint,
        radarSitesToBeDrawn,
        globeviewUtilityService.getFlightDotsSizes().flightDotRadius,
        projection,
      );
    }
    return null;
  };
  content.onGlobePanning = () => {
    if (clickLngLatPoint) {
      const newPosition = projection([clickLngLatPoint.lng, clickLngLatPoint.lat]);
      content.positionTooltip(newPosition);
    }
  };
  const getDirection = (
    positionCoordinates,
    tooltipHeight,
    tooltipWidth,
    bubbleOffset,
    stormDirection,
  ) => {
    const y = positionCoordinates[1];
    if (stormDirection) {
      if (stormDirection === 'storm-nw' || stormDirection === 'storm-ne') {
        return 'bottom';
      }
      return 'top';
    }
    if (y > tooltipHeight + bubbleOffset) {
      return 'top';
    }
    return 'bottom';
  };
  content.positionTooltip = (positionCoordinates) => {
    const { isAirportTooltip, stormDirection, stormSelected } = selectedTooltip;
    const hideBottomAxis = utilityService.hideBottomAxis();
    const tooltipHeight = bnd3Service.getSelectionHeight(d3.select('#globviewtooltip'));
    const tooltipWidth = bnd3Service.getSelectionWidth(d3.select('#globviewtooltip'));
    if (refAirportTooltipPoint) {
      positionCoordinates = projection(refAirportTooltipPoint);
    }
    const bubbleOffset = 15;
    let topValue = positionCoordinates[1];
    let leftValue = positionCoordinates[0];
    const direction = getDirection(
      positionCoordinates,
      tooltipHeight,
      tooltipWidth,
      bubbleOffset,
      stormDirection,
    );
    if (hideBottomAxis) {
      leftValue += utilityService.getSliderHeight();
    }
    switch (direction) {
      case 'bottom':
        topValue += bubbleOffset;
        break;
      default:
        topValue -= tooltipHeight;
        topValue -= bubbleOffset;
    }
    leftValue -= tooltipWidth / 2;
    const clickPointIsInsideBox = () => {
      const check = positionCoordinates[0] >= 0 && positionCoordinates[0] <= width;
      return check;
    };
    if (clickPointIsInsideBox()) {
      if (leftValue < 0) {
        leftValue = 0;
      } else if (leftValue + tooltipWidth > width) {
        leftValue = width - tooltipWidth;
      }
    } else {
      leftValue = -tooltipWidth;
    }
    const getClassOfTooltip = () => {
      let cssClass = isAirportTooltip ? 'isAirportTooltip' : '';
      if (direction === 'bottom') {
        cssClass += ' bottom';
      }
      return cssClass;
    };
    const getBubbleShift = () => {
      let shift = -30;
      if (clickPointIsInsideBox()) {
        shift = `${positionCoordinates[0] - leftValue}px`;
      }
      return shift;
    };
    d3.select('.mapview-bubble-container')
      .style('left', getBubbleShift())
      .style('margin-left', '-20px');
    d3.select('#globviewtooltip')
      .style('left', `${leftValue}px`) // Todo: fix this hardcoded
      .style('top', `${topValue}px`)
      .attr('class', getClassOfTooltip());

    drawStormTiming(projection, stormTrackCanvasConfig.ctx, stormSelected, dateFormat);
  };
  content.renderTooltip = (tooltipContent, refPoint) => {
    d3.select('#globviewtooltip-container').html(globeviewUtilityService.getBaseTooltipHtml());
    d3.select('#popup-close-button').on('click', () => {
      content.clearTooltip();
      resetTooltip();
      content.refreshGlobeCallFromSlider();
    });
    d3.select('#globviewtooltip #content').html(tooltipContent);
    content.positionTooltip(mouseClickPoints, refPoint);
  };
  content.getTooltipHTML = () => {
    setDateFormat();
    resetTooltip();
    refAirportTooltipPoint = null;
    clickLngLatPoint = projection.invert(mouseClickPoints);
    clickLngLatPoint = {
      lng: clickLngLatPoint[0],
      lat: clickLngLatPoint[1],
      x: mouseClickPoints[0],
      y: mouseClickPoints[1],
    };
    let matchedMosAirportForTooltip;
    let matchedMetarAirportForTooltip;
    let matchedMetsForTooltip;
    let matchedStormsForTooltip;
    const matchedTravelRouteAirportForTooltip = content.findMatchedTravelRouteAirportAtClickPoint(
      clickLngLatPoint,
    );
    if (!matchedTravelRouteAirportForTooltip) {
      matchedMosAirportForTooltip = content.findMatchedMosAirportAtClickPoint(clickLngLatPoint);
      const isFirstSlide = content.isFirstEtdSlide();
      if (isFirstSlide) {
        const matchedMetar = content.findMatchedMetarAirportAtClickPoint(clickLngLatPoint);
        if (matchedMetar) {
          matchedMosAirportForTooltip = null;
        }
      }
      if (!matchedMosAirportForTooltip) {
        matchedStormsForTooltip = content.findMatchedStormAtClickPoint(clickLngLatPoint);
        if (!matchedStormsForTooltip) {
          matchedMetarAirportForTooltip = content.findMatchedMetarAirportAtClickPoint(
            clickLngLatPoint,
          );
          if (!matchedMetarAirportForTooltip) {
            matchedMetsForTooltip = content.findMatchedMetsAtClickPoint(clickLngLatPoint);
          }
        }
      }
    }
    let { flightMode } = routeProfileService.getLampOrMavObj(content.sourceData, content.settings);
    if (flightMode) {
      flightMode = flightMode.toLowerCase();
    }
    if (matchedTravelRouteAirportForTooltip) {
      // First look for travel route airport
      matchedTravelRouteAirportForTooltip.flightMode = flightMode;
      // eslint-disable-next-line max-len
      const travelRouteAirportTooltipHtml = globeviewUtilityService.fetchTravelRouteAirportTooltipContentHtml(
        matchedTravelRouteAirportForTooltip,
        content.settings,
      );
      selectedTooltip.enableRadarFeature = true;
      refAirportTooltipPoint = [
        matchedTravelRouteAirportForTooltip.lng,
        matchedTravelRouteAirportForTooltip.lat,
      ];
      content.renderTooltip(travelRouteAirportTooltipHtml);
    } else if (matchedMosAirportForTooltip) {
      // Then look for MOS route airport
      matchedMosAirportForTooltip.flightMode = flightMode;
      globeviewUtilityService
        .fetchMosAirportTooltipContentHtml(matchedMosAirportForTooltip, content.settings)
        .then((htmlContent) => {
          selectedTooltip.isAirportTooltip = true;
          refAirportTooltipPoint = [
            matchedMosAirportForTooltip.lng,
            matchedMosAirportForTooltip.lat,
          ];
          content.renderTooltip(htmlContent);
        });
    } else if (matchedMetarAirportForTooltip) {
      globeviewUtilityService
        .fetchMetarTooltipContentHtml(matchedMetarAirportForTooltip, content.settings)
        .then((metarTooltipHtml) => {
          selectedTooltip.isAirportTooltip = true;
          refAirportTooltipPoint = [
            matchedMetarAirportForTooltip.lng,
            matchedMetarAirportForTooltip.lat,
          ];
          content.renderTooltip(metarTooltipHtml);
        });
    } else if (matchedMetsForTooltip && matchedMetsForTooltip.length) {
      // Then look for METs
      const metTooltipHtml = globeviewUtilityService.getMetPopoverHtml(
        matchedMetsForTooltip,
        dateFormat,
      );
      selectedTooltip.isAirportTooltip = false;
      content.renderTooltip(metTooltipHtml);
    } else if (matchedStormsForTooltip) {
      // Then look for Storms
      const stormTooltipHtml = globeviewUtilityService.getStormPopoverHtml(
        matchedStormsForTooltip,
        dateFormat,
      );
      refAirportTooltipPoint = [
        matchedStormsForTooltip.fcst[0][1],
        matchedStormsForTooltip.fcst[0][0],
      ];
      selectedTooltip.isAirportTooltip = true;
      selectedTooltip.stormDirection = matchedStormsForTooltip.stormDirection;
      selectedTooltip.stormSelected = matchedStormsForTooltip;
      content.renderTooltip(stormTooltipHtml);
    } else {
      content.clearTooltip();
    }
  };

  function appendCanvasAndConfigure(parentSelector, refCanvasConfig, canvasConfig, canvasId) {
    configureCanvas(
      parentSelector,
      refCanvasConfig,
      canvasConfig,
      canvasId,
      d3.geoPath,
      projection,
    );
  }

  function clearCanvasContainers() {
    d3.select('#map-canvas-globe').html('');
    d3.select('#map-canvas-radar').html('');
    d3.select('#map-canvas-stormtracks').html('');
    d3.select('#map-canvas-allradarsites').html('');
    d3.select('#map-canvas-features').html('');
  }

  function zoomByProjection(transform, isZoomEnd) {
    projection.translate([transform.x, transform.y]);
    projection.scale(transform.k);
    if (isZoomEnd) {
      requestDrawing({
        shouldDrawGlobe: false,
        shouldDrawRadar: true,
        isZoomEnd: true,
        shouldCleanRadar: true,
        shouldDrawFeatures: true,
        shouldFetchFreshRecords: true,
      });
    } else {
      requestDrawing({
        shouldDrawGlobe: true,
        shouldDrawRadar: true,
        shouldDrawFeatures: true,
        shouldCleanRadar: true,
      });
    }
  }
  content.initializeRequiredGlobeDependency = function () {
    clearCanvasContainers();
    setDimensions();
    setScales();
    let timeout;
    const canvasConfig = getHiDPICanvasConfig(width, height);
    // appendCanvasAndConfigure("#map-canvas", globeCanvasConfig, canvasConfig);
    appendCanvasAndConfigure('#map-canvas-globe', globeCanvasConfig, canvasConfig);
    // appendCanvasAndConfigure("#map-canvas-radar", radarCanvasConfig, canvasConfig);
    radarCanvasCollection.forEach((radarCanvasItem, index) => {
      appendCanvasAndConfigure('#map-canvas-radar', radarCanvasItem, canvasConfig, index);
    });
    appendCanvasAndConfigure('#map-canvas-features', featuresCanvasConfig, canvasConfig);
    appendCanvasAndConfigure('#map-canvas-stormtracks', stormTrackCanvasConfig, canvasConfig);
    appendCanvasAndConfigure('#map-canvas-allradarsites', allRadarSiteCanvasConfig, canvasConfig);
    requestDrawing({
      shouldDrawGlobe: true,
      shouldDrawRadar: false,
      shouldDrawFeatures: false,
      shouldCleanRadar: true,
    });
    d3.select('.spinner-globe').style('display', 'none');
    // ctx.imageSmoothingEnabled = false;
    const zoom2DMap = d3
      .zoom()
      .scaleExtent([300, 200000])
      .on('zoom', (event) => {
        zooming = true;
        clearTimeout(timeout);
        zoomByProjection(event.transform);
        content.onGlobePanning();
      })
      .on('end', (event) => {
        const { transform } = event;
        timeout = setTimeout(() => {
          zooming = false;
          zoomByProjection(transform, true);
        }, 500);
      });
    d3.select(globeCanvasConfig.ctx.canvas).call(zoom2DMap);

    d3.select('#map-canvas-globe canvas').on('click', (d3Event) => {
      mouseClickPoints = d3.pointer(d3Event);
      if (content.sourceData) {
        // globeviewUtilityService.calculateBoundingBox(projection, width, height);
        content.getTooltipHTML(mouseClickPoints);
      }
    });
  };
  content.clearTooltip = () => {
    d3.select('#globviewtooltip-container').html('');
  };
  content.refreshGlobeAndGetInBound = (dontGetInBound) => {
    requestDrawing({
      shouldDrawGlobe: true,
      shouldDrawRadar: true,
      shouldDrawAllRadar: true,
      shouldDrawFeatures: true,
      shouldGetInBoundToRoute: !dontGetInBound,
      shouldFetchFreshRecords: true,
      shouldCleanRadar: true,
      isZoomEnd: true,
    });
  };
  /**
   * This is called when button on map to recenter is called
   */
  content.recenterMapToRoute = () => {
    // * @property {Boolean} shouldCleanAllRadar
    // * @property {Boolean} shouldDrawAllRadar
    requestDrawing({
      shouldDrawGlobe: true,
      shouldDrawRadar: true,
      shouldDrawFeatures: true,
      shouldGetInBoundToRoute: true,
      shouldFetchFreshRecords: true,
      shouldCleanRadar: true,
      shouldCleanAllRadar: true,
      shouldDrawAllRadar: true,
      isZoomEnd: true,
    });
  };
  /**
   * This is called when button on map to recenter is called
   */
  content.recenterMapToCurrentLocation = () => {
    // * @property {Boolean} shouldCleanAllRadar
    // * @property {Boolean} shouldDrawAllRadar
    requestDrawing({
      shouldDrawGlobe: true,
      shouldDrawRadar: true,
      shouldDrawFeatures: true,
      shouldGetInBoundToRoute: false,
      shouldGetInBoundToCurrentPosition: true,
      shouldFetchFreshRecords: true,
      shouldCleanRadar: true,
      shouldCleanAllRadar: true,
      shouldDrawAllRadar: true,
      isZoomEnd: true,
    });
  };
  content.refreshGlobeCallFromSlider = (isSliderDragEnd = false) => {
    content.clearTooltip();
    requestDrawing({
      shouldDrawGlobe: false,
      shouldDrawRadar: isSliderDragEnd,
      shouldDrawFeatures: !isSliderDragEnd, // no need to draw features on slider drag end
    });
  };
  content.loadGlobeStaticData = async () => {
    const data = await globeviewUtilityService.getGlobeStaticData();
    // land = data.land;
    usaStates = data.usaStates;
  };
  content.onMetChanged = () => {
    requestDrawing({
      shouldDrawGlobe: false,
      shouldCleanRadar: true,
      shouldDrawRadar: true,
      shouldDrawFeatures: true,
    });
  };
  return content;
}

export default globeviewService();
